import type { ReactNode } from 'react';

import { BLOCK_CLAUSE_FIELD_DEFS, EMPTY_OPTIONS_RECORD, SORTED_CONTRACT_DETAILS_FIELD_DEFS } from '../constants';
import { useQueryBuilderStore } from '../store';
import type { FieldDef } from '../types';
import { CDR_SCHEMA } from '../utils/cdr-schema';
import { deriveFieldDef } from '../utils/derive-field-defs';
import { isRowComplete } from '../utils/derive-query-filters';
import { QueryBuilderPanel } from '../views/QueryBuilderPanel';

const datapointFieldDefMap: Map<string, FieldDef> = new Map(
  CDR_SCHEMA.flatList.map((node) => [node.id, deriveFieldDef(node)]),
);

const MAX_FILTERS_COUNT = 10;

interface QueryBuilderControllerProps {
  entityName: string;
  headerSlot?: ReactNode;
  footerSlot?: ReactNode;
}

export function QueryBuilderController({ entityName, headerSlot, footerSlot }: QueryBuilderControllerProps) {
  const { addRow, removeRow, updateRow, clearRows, setPanelOpen } = useQueryBuilderStore();
  const rows = useQueryBuilderStore((s) => s.rowsByEntity[entityName] ?? []);

  const optionsRecord = useQueryBuilderStore((s) => s.optionsRecordByEntity[entityName] ?? EMPTY_OPTIONS_RECORD);
  // Gated while any row is unconfigured so empty rows can't pile up between filters
  const disableAddFilter = rows.length >= MAX_FILTERS_COUNT || rows.some((row) => !isRowComplete(row));

  return (
    <QueryBuilderPanel
      disableAddFilter={disableAddFilter}
      rows={rows}
      headerSlot={headerSlot}
      footerSlot={footerSlot}
      contractDetailsFields={SORTED_CONTRACT_DETAILS_FIELD_DEFS}
      blockFields={BLOCK_CLAUSE_FIELD_DEFS}
      datapointTree={CDR_SCHEMA.tree}
      datapointFieldDefMap={datapointFieldDefMap}
      optionsRecord={optionsRecord}
      onAddRow={() => addRow(entityName)}
      onRemoveRow={(rowId) => removeRow(entityName, rowId)}
      onClearAll={() => clearRows(entityName)}
      onHide={() => setPanelOpen(entityName, false)}
      onRowUpdate={(rowId, patch) => updateRow(entityName, rowId, patch)}
    />
  );
}
